import { useEffect } from 'react';
import { css } from '@emotion/react';
import { ThreadResult } from '@weave/schema-gen-ts/dist/schemas/sms/search/v2/sms_search_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import {
  GlobalSearch,
  GlobalSearchModuleOrder,
  GlobalSearchTrackingIds,
  useGlobalSearch,
  useGlobalRecentSearches,
} from '@frontend/global-search';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatResultsPrimaryKeys, handleRegisterClick, handleUpdateSearch } from '@frontend/search-history';
import { theme } from '@frontend/theme';
import { NakedUl, SkeletonLoader, Text } from '@frontend/design-system';
import { useInboxSearch } from './hooks/use-inbox-search';
import { MessageSearchResult } from './message-search-result';

const defaultMaxResults = 5;
const entity = Entity.ENTITY_SMS;

type MessagesGlobalSearchModuleProps = ReturnType<typeof useInboxSearch>;
export const MessagesGlobalSearchModule = ({ messagesQuery, logSearch }: MessagesGlobalSearchModuleProps) => {
  const { t } = useTranslation('inbox');
  const { debouncedSearchTerm, viewAllFeature, close } = useGlobalSearch([
    'debouncedSearchTerm',
    'viewAllFeature',
    'close',
  ]);

  const { addSearch } = useGlobalRecentSearches();

  const { selectedLocationIds } = useAppScopeStore();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const aclEnforcementEnabled = getFeatureFlagValue('nwx:messaging-inbox-fe-acl-enforcement', 'ALL');
  const locationIdsWithACL = aclEnforcementEnabled
    ? filterLocationIdsByACLs({
        locationIds: selectedLocationIds,
        acls: Permission.SMS_INBOX_MANAGE,
      })
    : selectedLocationIds;
  const showNoAccessState = aclEnforcementEnabled && locationIdsWithACL.length === 0 && !!selectedLocationIds.length;

  useEffect(() => {
    if (messagesQuery.isLoading.firstPage) return;
    handleUpdateSearch({
      entity,
      results: formatResultsPrimaryKeys<ThreadResult>('threadId', messagesQuery.results),
    });
  }, [messagesQuery.results, messagesQuery.isLoading.firstPage]);

  const maxResults = viewAllFeature === 'messaging' ? messagesQuery.results.length : 5;

  return (
    <GlobalSearch.Section.Content
      showViewAll={!showNoAccessState && messagesQuery.results.length > defaultMaxResults}
      hasResults={!!messagesQuery.results.length}
      count={showNoAccessState ? null : `${messagesQuery.total}${messagesQuery.hasNextPage ? '+' : ''}`}
    >
      {showNoAccessState ? (
        <Text color='subdued' css={{ width: '100%', textAlign: 'center' }}>
          {t('You do not have permission to view text messages')}
        </Text>
      ) : (
        <NakedUl css={{ display: 'flex', flexDirection: 'column' }}>
          {messagesQuery.isLoading.firstPage ? (
            <div>
              {Array.from({ length: defaultMaxResults }).map((_, index) => (
                <MessageItemSkeletonLoader key={index} />
              ))}
            </div>
          ) : messagesQuery.results.length ? (
            messagesQuery.results.slice(0, maxResults).map(({ messages, ...thread }, index) => {
              const message = messages[0];
              if (!message) return null;

              return (
                <MessageSearchResult
                  key={message.smsId}
                  data-trackingid={GlobalSearchTrackingIds.searchResultItem('messaging')}
                  onSelect={() => {
                    logSearch(debouncedSearchTerm);
                    addSearch(debouncedSearchTerm, 'messaging');
                    handleRegisterClick({
                      entity,
                      entityId: thread.threadId,
                      entityPosition: GlobalSearchModuleOrder.messaging,
                      itemPosition: index,
                    });
                    close();
                  }}
                  {...thread}
                  message={message}
                  searchValue={debouncedSearchTerm}
                  hideBottomBorder={index === maxResults - 1 || index === messagesQuery.results.length - 1}
                />
              );
            })
          ) : (
            <GlobalSearch.EmptyState />
          )}
        </NakedUl>
      )}
    </GlobalSearch.Section.Content>
  );
};

const MessageItemSkeletonLoader = () => {
  return (
    <div
      css={css`
        display: flex;
        width: 100%;
        align-items: center;
        height: 60px;
        padding: ${theme.spacing(1)};
        gap: ${theme.spacing(2)};

        :not(:last-child) {
          border-bottom: 1px solid ${theme.colors.neutral10};
        }
      `}
    >
      <SkeletonLoader
        css={css`
          flex-shrink: 0;
        `}
        shape='circle'
        width={40}
        height={40}
      />
      <div
        css={css`
          width: 100%;
        `}
      >
        <SkeletonLoader
          css={css`
            margin-bottom: ${theme.spacing(1)};
          `}
          height={20}
          width='40%'
        />
        <SkeletonLoader width='100%' height={18} />
      </div>
    </div>
  );
};
